import { useEffect, useState } from 'react';
import { Box, Heading, Text, Spinner, Alert, AlertIcon, Badge, Stat, StatLabel, StatNumber, StatHelpText, VStack, HStack, Button, useColorModeValue, Center, Image, Text as ChakraText, Circle } from '@chakra-ui/react';
import { Link, useParams } from 'react-router-dom';
import BuildDetail from './BuildDetail';
import { saveAs } from 'file-saver'; // Add this import at the top
import SpdxWidget from './SpdxWidget';
import SarifWidget from './SarifWidget';
import CveWidget from './CveWidget';
import TestResultsWidget from './TestResultsWidget';
import { ExternalLinkIcon } from '@chakra-ui/icons';

// GradeIndicator component (copied from Dashboard.js)
const GradeIndicator = ({ grade, label }) => {
  const boxBg = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const circleBg = useColorModeValue('gray.300', 'gray.600');
  
  // Define colors for each grade
  const gradeAColor = useColorModeValue('green.500', 'green.400');
  const gradeBColor = useColorModeValue('yellow.500', 'yellow.400');
  const gradeCColor = useColorModeValue('orange.500', 'orange.400');
  const gradeDColor = useColorModeValue('red.500', 'red.400');
  const gradeEColor = useColorModeValue('orange.600', 'orange.500'); // Dark orange for E
  const gradeFColor = useColorModeValue('red.500', 'red.400'); // Red for F
  const defaultColor = useColorModeValue('gray.500', 'gray.400');
  
  // Get color based on grade
  const getGradeColor = (grade) => {
    switch (grade.toUpperCase()) {
      case 'A': return gradeAColor;
      case 'B': return gradeBColor;
      case 'C': return gradeCColor;
      case 'D': return gradeDColor;
      case 'E': return gradeEColor;
      case 'F': return gradeFColor;
      default: return defaultColor;
    }
  };
  
  const gradeColor = getGradeColor(grade);
  const isFGrade = grade.toUpperCase() === 'F';
  
  return (
    <Box
      bg={boxBg}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      px={2}
      py={1}
      minW="90px"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
    >
      <Text fontSize="xs" color={textColor} fontWeight="bold" letterSpacing="wider" mr={2}>
        {label.toUpperCase()}
      </Text>
      <Circle 
        size="24px" 
        bg={isFGrade ? circleBg : gradeColor}
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="none"
      >
        <Text
          fontSize="xs"
          fontWeight="bold"
          color={isFGrade ? textColor : 'white'}
          textShadow={isFGrade ? 'none' : '2px 2px 4px rgba(0,0,0,0.9)'}
        >
          {grade}
        </Text>
      </Circle>
    </Box>
  );
};

function DependencyRow({ dep, descText, boxBg }) {
  const name = dep.productName || dep.productId;
  return (
    <Box
      as={Link}
      to={`/repo/${dep.productId}`}
      p={0}
      _hover={{ textDecoration: 'none', bg: boxBg === 'gray.50' ? 'gray.100' : 'gray.700' }}
      borderRadius="md"
      boxShadow="sm"
      display="block"
    >
      <HStack spacing={3} p={2}>
        <img
          src={`/api/icon/${dep.productId || 'default'}`}
          alt="icon"
          style={{ width: 28, height: 28, borderRadius: 4 }}
          onError={e => { e.target.src = '/api/icon/default'; }}
        />
        <Text fontWeight="medium">{name}</Text>
      </HStack>
    </Box>
  );
}

// Net Compatibility Widget
function NetCompabilityWidget({ hideTitle }) {
  return (
    <Box p={4} borderWidth={1} borderRadius="lg" boxShadow="md" bg="gray.800" color="white" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
      {!hideTitle && <Text fontSize="lg" fontWeight="bold" mb={1}>.NET Core Compatibility</Text>}
      <Image src="https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/72x72/1f63c.png" alt="Sleeping Cat" boxSize="64px" mb={2} />
      <Text fontSize="md" color="gray.300">Coming soon…</Text>
    </Box>
  );
}

export default function RepoDetail({ repository }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allRepos, setAllRepos] = useState([]);
  const tableBg = useColorModeValue('white', 'gray.800');
  const tableText = useColorModeValue('black', 'white');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const boxBg = useColorModeValue('gray.50', 'gray.900');
  const descText = useColorModeValue('gray.600', 'gray.300');

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`/api/repos/${repository}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch repo details');
        return res.json();
      })
      .then((d) => {
        setData(d);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [repository]);

  useEffect(() => {
    fetch('/api/repos')
      .then(res => res.ok ? res.json() : [])
      .then(setAllRepos)
      .catch(() => setAllRepos([]));
  }, []);

  // SPDX download handler
  const handleSpdxDownload = async (repo, buildId, version, fileName) => {
    try {
      const res = await fetch(`/api/spdx/${repo}/${buildId}/${version}/${fileName}`);
      if (!res.ok) throw new Error('Failed to fetch SPDX file');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        a.remove();
      }, 100);
    } catch (err) {
      // Optionally handle error
    }
  };

  if (loading) return <Center minH="60vh"><Spinner size="xl" /></Center>;
  if (error) return <Alert status="error" mt={10}><AlertIcon />Error: {error}</Alert>;
  if (!data) return null;

  // Hardcode all products except Central Administration to depend on Central Administration
  const isCentralAdmin = data.repository === 'CentralAdministration';
  const hardcodedDependency = {
    productId: 'c24ce33b-662f-e811-3185-00155db46002',
    productName: 'Central Administration',
    icon: `/api/icon/c24ce33b-662f-e811-3185-00155db46002`,
  };
  const dependencies = isCentralAdmin
    ? []
    : [hardcodedDependency];

  const dependencyInList = allRepos.filter(repo =>
    Array.isArray(repo.manifest?.dependencies) &&
    repo.manifest.dependencies.some(dep => dep.productId === data.id)
  );

  return (
    <Box maxW="900px" mx="auto" mt={10} mb={20} p={4} bg={boxBg} borderRadius="md">
      <Button as={Link} to="/" mb={4} colorScheme="teal" variant="outline">← Back to Dashboard</Button>
      <Box p={6} bg={tableBg} color={tableText} borderRadius="md" boxShadow="md" mb={6}>
        <HStack mb={2} align="center" justify="space-between">
          <HStack align="center">
            <img
              src={`/api/icon/${data.repository}`}
              alt="icon"
              style={{ width: 40, height: 40, borderRadius: 6, display: 'inline-block', verticalAlign: 'middle', marginRight: 12 }}
              onError={e => { e.target.src = '/api/icon/default'; }}
            />
            <Heading size="lg" mb={0}>
              {data.manifest?.name || data.title} <Badge colorScheme="teal">{data.latestBuildId || data.repository}</Badge>
            </Heading>
          </HStack>
          {/* Action Buttons */}
          <HStack spacing={3}>
            <Button
              size="sm"
              colorScheme="blue"
              variant="outline"
              leftIcon={<ExternalLinkIcon />}
              onClick={() => window.open(`https://portal.labtagon.com/products/${data.id}`, '_blank')}
            >
              Product Page
            </Button>
            <Button
              size="sm"
              colorScheme="green"
              variant="outline"
              leftIcon={<ExternalLinkIcon />}
              onClick={() => window.open(`https://docs.labtagon.com/products/${data.repository}`, '_blank')}
            >
              Documentation
            </Button>
          </HStack>
        </HStack>
        {/* Grade Indicators under the buttons */}
        <Box mt={3} mb={2} display="flex" justifyContent="flex-end">
          <HStack spacing={2} align="center">
            <GradeIndicator grade={data.grades?.sast || 'A'} label="SAST" />
            <GradeIndicator grade={data.grades?.license || 'A'} label="LICENSE" />
            <GradeIndicator grade={data.grades?.cve || 'A'} label="CVE" />
          </HStack>
        </Box>
        {/* Matrix42WSM Versions Display */}
        {(() => {
          const versions = Array.isArray(data.versions) ? data.versions : [];
          
          if (!versions.length) return null;
          
          return (
            <HStack spacing={2} mb={4} flexWrap="wrap" align="center">
              {versions.map(version => (
                <Badge key={version} colorScheme="blue" variant="outline" fontSize="sm">
                  {version}
                </Badge>
              ))}
            </HStack>
          );
        })()}
        <Text color={descText} mb={2}>{data.manifest?.description || 'No description available.'}</Text>
        {(dependencies.length > 0) && (
          <Box mt={4} mb={2}>
            <Text fontWeight="bold" mb={2}>Dependencies:</Text>
            <VStack align="stretch" spacing={2}>
              {dependencies.map((dep, i) => (
                <DependencyRow key={i} dep={dep} descText={descText} boxBg={boxBg} />
              ))}
            </VStack>
          </Box>
        )}
        {dependencyInList.length > 0 && (
          <Box mt={4} mb={2}>
            <Text fontWeight="bold" mb={2}>Dependency in:</Text>
            <VStack align="stretch" spacing={2}>
              {dependencyInList.map((repo, i) => (
                <Box
                  as={Link}
                  to={`/repo/${repo.repository}`}
                  p={0}
                  _hover={{ textDecoration: 'none', bg: boxBg === 'gray.50' ? 'gray.100' : 'gray.700' }}
                  borderRadius="md"
                  boxShadow="sm"
                  display="block"
                  key={repo.repository}
                >
                  <HStack spacing={3} p={2}>
                    <img
                      src={`/api/icon/${repo.id || 'default'}`}
                      alt="icon"
                      style={{ width: 28, height: 28, borderRadius: 4 }}
                      onError={e => { e.target.src = '/api/icon/default'; }}
                    />
                    <Text fontWeight="medium">{repo.manifest?.productName || repo.manifest?.name || repo.title}</Text>
                  </HStack>
                </Box>
              ))}
            </VStack>
          </Box>
        )}
        {/* Remove build count Stat group from here */}
      </Box>
      {/* --- WIDGETS FOR LATEST BUILD/VERSION --- */}
      {(() => {
        // Use the new data structure where reports are in data.reports
        const reports = data.reports || {};
        const latestBuildId = data.latestBuildId;
        const versions = Array.isArray(data.versions) ? data.versions : [];
        const latestVersion = versions[0];
        // SBOM findings from Bomber, if present
        const sbomFindings = reports.SBOMReport && reports.SBOMReport.findings ? reports.SBOMReport.findings : undefined;
        return (
          <>
            {/* SPDX full width */}
            {reports.SBOMReport && (
              <Box mb={4}>
                <Heading size="md" mb={4} mt={2}>SPDX Report</Heading>
                <SpdxWidget
                  spdxJson={reports.SBOMReport}
                  repo={data.repository}
                  build={latestBuildId}
                  filename={reports.SBOMReport.FileName}
                  version={latestVersion}
                  findings={sbomFindings}
                  bomber={reports.BomberReport}
                />
              </Box>
            )}
            {/* SARIF and .NET Core Compatibility side by side, each 50% width */}
            {reports.SARIFReport && (
              <HStack spacing={4} align="stretch" mb={8} flexWrap="wrap">
                <Box flex="1 1 0" minW={{ base: '100%', md: '0' }} maxW={{ base: '100%', md: '50%' }} display="flex" flexDirection="column">
                  <>
                    <Heading size="md" mb={4} mt={2}>Security Findings</Heading>
                    <Box flex="1 1 0" display="flex" flexDirection="column" height="100%" minHeight="220px">
                      <SarifWidget sarifJson={reports.SARIFReport} />
                    </Box>
                  </>
                </Box>
                <Box flex="1 1 0" minW={{ base: '100%', md: '0' }} maxW={{ base: '100%', md: '50%' }} display="flex" flexDirection="column">
                  <Heading size="md" mb={4} mt={2}>.NET Core Compatibility</Heading>
                  <Box flex="1 1 0" display="flex" flexDirection="column" height="100%" minHeight="220px">
                    <NetCompabilityWidget hideTitle />
                  </Box>
                </Box>
              </HStack>
            )}
            {/* Always show .NET Core Compatibility widget below if SARIF is not present, but without minHeight and margin-bottom */}
            {!reports.SARIFReport && (
              <Box>
                <Heading size="md" mb={4} mt={2}>.NET Core Compatibility</Heading>
                <Box flex="1 1 0" display="flex" flexDirection="column" height="100%">
                  <NetCompabilityWidget hideTitle />
                </Box>
              </Box>
            )}
            {/* Test Results Widget */}
            {reports.TestResult && (
              <Box mb={4}>
                <Heading size="md" mb={4} mt={2}>Test Results</Heading>
                <TestResultsWidget testResult={reports.TestResult} />
              </Box>
            )}
          </>
        );
      })()}
      {/* --- BUILDS TABLE --- */}
      <Box mt={8}>
        <Heading size="md" mb={4}>Builds</Heading>
        <Box overflowX="auto">
          <table style={{ width: '100%', borderCollapse: 'collapse', background: 'none' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
                <th style={{ textAlign: 'left', padding: '8px', color: descText }}>Build</th>
                <th style={{ textAlign: 'left', padding: '8px', color: descText }}>Versions</th>
                <th style={{ textAlign: 'left', padding: '8px', color: descText }}>SPDX</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #222' }}>
                <td style={{ padding: '8px', fontWeight: 500 }}>{data.latestBuildId}</td>
                <td style={{ padding: '8px' }}>{Array.isArray(data.versions) ? data.versions.join(', ') : 'N/A'}</td>
                <td style={{ padding: '8px' }}>
                  {data.reports?.SBOMReport && data.reports.SBOMReport.FileName ? (
                    <Button size="sm" colorScheme="teal" variant="outline"
                      onClick={() => handleSpdxDownload(data.repository, data.latestBuildId, data.versions && data.versions[0], data.reports.SBOMReport.FileName)}>
                      Download SPDX
                    </Button>
                  ) : (
                    <Text color={descText} fontSize="sm">-</Text>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </Box>
      </Box>
      {/* --- FOOTER WITH CACHE TIMESTAMP --- */}
      <Box mt={8} textAlign="right">
        <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wider">
          {data.cacheTimestamp ? new Date(data.cacheTimestamp).toLocaleString().toUpperCase() : 'UNKNOWN'}
        </Text>
      </Box>
    </Box>
  );
} 